import { Box, LuxTextArea } from '@gemini/fe-ds-core';
import { yupResolver } from '@hookform/resolvers/yup';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { memo, useEffect, useMemo, useRef } from 'react';
import { Controller, Resolver, useForm } from 'react-hook-form';
import { getInsuranceFieldErrorProps } from '../InsurancesForm.utils';
import {
  CustomInsuranceFieldsFormData,
  CustomInsuranceFieldsFormProps,
} from './CustomInsuranceFieldsForm.interface';
import { customInsuranceFieldsFormSchema } from './CustomInsuranceFieldsForm.schema';
import {
  CUSTOM_INSURANCE_FIELD_MAX_LENGTH,
  getVisibleCustomInsuranceFields,
  toCustomInsuranceDefaultValues,
} from './CustomInsuranceFieldsForm.utils';

const CustomInsuranceFieldsForm = ({
  currentIndex,
  customFields,
  initialValues,
  isVisible = true,
  onValidate,
  onChange,
  onCommit,
}: CustomInsuranceFieldsFormProps) => {
  const brandName = useOnlineBookingStore(state => state.brandName);
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);
  const validationMode = brandName ? 'onBlur' : 'onChange';

  const visibleFields = useMemo(
    () => getVisibleCustomInsuranceFields(customFields),
    [customFields],
  );

  const {
    control,
    getValues,
    trigger,
    watch,
    formState: { isValid },
  } = useForm<CustomInsuranceFieldsFormData>({
    mode: validationMode,
    reValidateMode: validationMode,
    resolver: yupResolver(
      customInsuranceFieldsFormSchema(customFields),
    ) as Resolver<CustomInsuranceFieldsFormData>,
    defaultValues: toCustomInsuranceDefaultValues(customFields, initialValues),
  });

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    if (!visibleFields.length) {
      onValidate(true, currentIndex);
      return;
    }

    void trigger();
  }, [currentIndex, onValidate, trigger, visibleFields.length]);

  useEffect(() => {
    onValidate(!visibleFields.length || isValid, currentIndex);
  }, [currentIndex, isValid, onValidate, visibleFields.length]);

  useEffect(() => {
    const subscription = watch(value => {
      onChangeRef.current?.(value as CustomInsuranceFieldsFormData);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    return () => {
      onCommitRef.current?.(getValues());
    };
  }, [getValues]);

  if (!visibleFields.length) {
    return null;
  }

  return (
    <Box
      padding="0.5rem 1rem 1rem 1rem"
      display={isVisible ? 'grid' : 'none'}
      gridTemplateColumns="1fr"
      gap="1rem"
    >
      {visibleFields.map(fieldConfig => (
        <Controller
          key={fieldConfig.fieldKey}
          name={
            `patientDetails.storeCustomFieldsInsurance.${fieldConfig.fieldKey}` as const
          }
          control={control}
          render={({ field, fieldState }) => (
            <LuxTextArea
              {...field}
              minRows={1}
              inputProps={{
                maxLength: CUSTOM_INSURANCE_FIELD_MAX_LENGTH,
              }}
              limit={CUSTOM_INSURANCE_FIELD_MAX_LENGTH}
              resizable
              fullWidth
              label={fieldConfig.label}
              required={fieldConfig.required}
              value={field.value || ''}
              {...getInsuranceFieldErrorProps(fieldState, validationMode)}
            />
          )}
        />
      ))}
    </Box>
  );
};

export default memo(CustomInsuranceFieldsForm);
